// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { EnvironmentInfo } from "@azure/communication-calling";
import { CallAdapterState, DeviceManagerState } from "@azure/communication-react";


export const _isPreviewOn = (deviceManager: DeviceManagerState): boolean =>
    // TODO: we should take in a LocalVideoStream that developer wants to use as their 'Preview' view. We should also
    // handle cases where 'Preview' view is in progress and not necessary completed.
     deviceManager.unparentedViews.length > 0 && deviceManager.unparentedViews[0].view !== undefined
;

/**
 * @private
 */
export const isCameraOn = (state: CallAdapterState): boolean => {
  if (state.call) {
    const stream = state.call.localVideoStreams.find((stream) => stream.mediaStreamType === 'Video');
    return !!stream;
  } else {
    if (state.devices.selectedCamera) {
      const previewOn = _isPreviewOn(state.devices);
      return previewOn;
    }
  }
  return false;
};

/* @conditional-compile-remove(call-readiness) */
/**
 * @returns Permissions state for the camera.
 */
const queryCameraPermissionFromPermissionsAPI = async (): Promise<PermissionState | 'unsupported'> => {
    try {
        return (await navigator.permissions.query({ name: 'camera' as PermissionName })).state;
    } catch (e) {
        console.warn('permissions API is not supported by browser', e);
        return 'unsupported';
    }
};

/* @conditional-compile-remove(call-readiness) */
/**
 * @returns Permissions state for the microphone.
 */
const queryMicrophonePermissionFromPermissionsAPI = async (): Promise<PermissionState | 'unsupported'> => {
    try {
        return (await navigator.permissions.query({ name: 'microphone' as PermissionName })).state;
    } catch (e) {
        console.warn('permissions API is not supported by browser', e);
        return 'unsupported';
    }
};

/**
 *
 * This function uses permission API to determine if device permission state is granted, prompt or denied
 * @returns whether device permission state is granted, prompt or denied
 * If permission API is not supported on this browser, permission state is set to unsupported.
 * @private
 */
export const getDevicePermissionState = async (
    setVideoState: (state: PermissionState | 'unsupported') => void,
    setAudioState: (state: PermissionState | 'unsupported') => void
): Promise<void> => {
    const [cameraResult, microphoneResult] = await Promise.all([
        queryCameraPermissionFromPermissionsAPI(),
        queryMicrophonePermissionFromPermissionsAPI()
    ]);
    setVideoState(cameraResult);
    setAudioState(microphoneResult);
};

export const isSafari = (environmentInfo: undefined | EnvironmentInfo): boolean => environmentInfo?.environment.browser.toLowerCase() === 'safari';

// noinspection PointlessBooleanExpressionJS
export const isUnsupportedEnvironment = (
    environmentInfo?: EnvironmentInfo,
    unsupportedBrowserVersionOptedIn?: boolean,
): boolean => !!(
        environmentInfo?.isSupportedBrowser === false ||
        (environmentInfo?.isSupportedBrowserVersion === false && !unsupportedBrowserVersionOptedIn) ||
        environmentInfo?.isSupportedPlatform === false
    );

export class Delayed {
    private lastUpdateTime = 0;
    private readonly throttleInterval = 50; //Set the desired interval between visual updates in milliseconds

    /** Wraps callback function to throttle it's execution
     * @param callback function to execute.
     * @returns closure around "callback" function
     */
    private delayed(callback: () => void) {
        return (currentTimestamp: number) => {
            // Check if enough time has passed since the last visual update
            if (currentTimestamp - this.lastUpdateTime >= this.throttleInterval) {
                callback();
                //Record the time of the current update
                this.lastUpdateTime = currentTimestamp;
            }
        }
    }

    requestAnimationFrame(callback: () => void) {
        window.requestAnimationFrame(
            this.delayed(callback)
        );
    }
}